import { ethers } from 'ethers';
import { toast } from 'react-toastify';
import { PERP_VAULT_INTERFACE_ABI } from '../EvmConstants';
import { evmToBech32Address } from './EvmContract';
import { setPerpsUpdate } from "../../ReduxSlices/TradeSlice";
import { getEvmExplorerUrl } from '../../config/network';
import { CLIENT_ORIGIN, enforceClientOrigin } from '../../utils/clientOrigin';
import {
  showTransactionToast,
  dismissTransactionToast,
  showTxSuccess,
  showTxError,
} from "../../Components/Toast/TransactionToast";

// ---------------------------------------------------------------------------
// Gasless transaction utilities
// Nibiru EVM uses legacy (type-0) transactions with zero gas price.
// We still need an accurate gasLimit to avoid out-of-gas reverts, so we
// estimate on-chain first and fall back to a generous static limit.
// ---------------------------------------------------------------------------
const FALLBACK_GAS_LIMIT = 5_000_000n;
const ZERO_GAS_PRICE = ethers.parseUnits("0", "gwei");

/**
 * Adds a 10 % buffer to a gas estimate to avoid out-of-gas reverts.
 * @param {bigint} estimate - Raw gas estimate from the node
 * @returns {bigint} Buffered gas limit
 */
const withGasBuffer = (estimate) => {
  if (estimate === 0n) return 1n;
  const buffered = (estimate * 11n) / 10n;
  return buffered > estimate ? buffered : estimate;
};

/**
 * Estimates gas for a transaction, falling back to a static limit on failure.
 * Gas is free on Nibiru (gasPrice 0), but an accurate gasLimit prevents
 * unnecessary OOG reverts.  Mirrors the pattern from the sai-evm e2e tests.
 *
 * @param {() => Promise<bigint>} estimateFn - Returns a gas estimate (e.g.
 *   `() => contract.myMethod.estimateGas(arg1, arg2)`)
 * @returns {Promise<Object>} Gas overrides for ethers.js contract calls
 */
export const estimateGasWithFallback = async (estimateFn) => {
  const zeroGas = { gasPrice: ZERO_GAS_PRICE };
  try {
    const estimate = await estimateFn();
    return { gasLimit: withGasBuffer(estimate), ...zeroGas };
  } catch (error) {
    console.warn("Gas estimation failed, using fallback:", error?.message || error);
    return { gasLimit: FALLBACK_GAS_LIMIT, ...zeroGas };
  }
};

/**
 * Executes a contract method with automatic gas estimation + fallback.
 * Handles the full estimate-then-send cycle in one call.
 *
 * @param {ethers.Contract} contract - Contract instance (with signer)
 * @param {string} method - Solidity method name
 * @param {Array} args - Arguments forwarded to the method
 * @returns {Promise<ethers.TransactionResponse>}
 */
export const executeWithGasEstimation = async (contract, method, args = []) => {
  const gasOverrides = await estimateGasWithFallback(
    () => contract[method].estimateGas(...args),
  );
  return contract[method](...args, gasOverrides);
};

/**
 * Returns static gas overrides for gasless EVM transactions.
 * Prefer estimateGasWithFallback / executeWithGasEstimation for dynamic
 * estimation.  Kept for backward compatibility.
 * @returns {Object} Gas override object for ethers.js contract calls
 */
export const getGasSettings = () => ({
  gasLimit: FALLBACK_GAS_LIMIT,
  gasPrice: ZERO_GAS_PRICE,
});

/**
 * Converts EVM address to bech32 address using the imported function
 * @param {string} evmAddress - EVM address (0x...)
 * @returns {Promise<string>} Bech32 address (nibi...)
 */
export const convertEvmToBech32 = async (evmAddress) => {
  try {
    return await evmToBech32Address(evmAddress);
  } catch (error) {
    console.error("Error converting EVM to bech32:", error);
    throw error;
  }
};

/**
 * Gets the current user's bech32 address from their EVM wallet
 * @returns {Promise<string>} Bech32 address
 */
export const getCurrentUserBech32Address = async () => {
  try {
    if (!window.ethereum) {
      throw new Error("MetaMask is not installed");
    }
    
    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const evmAddress = await signer.getAddress();
    
    return await convertEvmToBech32(evmAddress);
  } catch (error) {
    console.error("Error getting user bech32 address:", error);
    throw error;
  }
};

/**
 * Fetches ERC20 USDC balance directly from user's wallet
 * @param {string} account - User's EVM address
 * @returns {Promise<number>} USDC balance
 */
export const fetchEvmTokenBalance = async (account) => {
  try {
    if (!window.ethereum) {
      throw new Error("MetaMask is not installed");
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const usdcContractAddress = process.env.REACT_APP_NIBIRU_USDC_CONTRACT_ADDRESS;
    
    if (!usdcContractAddress) {
      console.error("USDC contract address not configured");
      return 0;
    }

    // Get balance directly using provider
    const balance = await provider.call({
      to: usdcContractAddress,
      data: ethers.concat([
        ethers.id("balanceOf(address)").slice(0, 10),
        ethers.AbiCoder.defaultAbiCoder().encode(["address"], [account])
      ])
    });

    const balanceDecoded = ethers.AbiCoder.defaultAbiCoder().decode(["uint256"], balance)[0];
    const formattedBalance = Number(ethers.formatUnits(balanceDecoded, 6)); // USDC has 6 decimals
    
    console.log(`ERC20 USDC Balance for ${account}:`, {
      raw: balanceDecoded.toString(),
      formatted: formattedBalance,
      contractAddress: usdcContractAddress
    });

    return formattedBalance;

  } catch (error) {
    console.error("Error fetching ERC20 USDC balance:", error);
    return 0;
  }
};

/**
 * Fetches bank USDC.e balance for a bech32 Nibiru address via LCD.
 * @param {string} bech32Address - Nibiru bech32 address (nibi1...)
 * @returns {Promise<number>} Bank USDC balance
 */
export const fetchBankBalance = async (bech32Address) => {
  if (!bech32Address || !bech32Address.startsWith("nibi")) return 0;

  try {
    const restEndpoint = (process.env.REACT_APP_MAINNET_REST || "https://lcd.nibiru.fi").replace(/\/+$/, "");
    const denom = process.env.REACT_APP_COLLATERAL_DENOM || "erc20/0x0829F361A05D993d5CEb035cA6DF3446b060970b";
    const url = `${restEndpoint}/cosmos/bank/v1beta1/balances/${bech32Address}/by_denom?denom=${encodeURIComponent(denom)}`;

    const res = await fetch(url);
    if (!res.ok) return 0;

    const data = await res.json();
    const amount = data?.balance?.amount;
    if (!amount) return 0;

    const formatted = Number(amount) / 1e6;
    console.log(`Bank USDC.e balance for ${bech32Address}:`, formatted);
    return formatted;
  } catch (err) {
    console.warn("Failed to fetch bank balance:", err.message);
    return 0;
  }
};

/**
 * Fetches combined ERC20 + bank USDC.e balance.
 * @param {string} evmAddress - EVM hex address (0x...)
 * @param {string} bech32Address - Nibiru bech32 address (nibi1...)
 * @returns {Promise<{erc20: number, bank: number, total: number}>}
 */
export const fetchCombinedBalance = async (evmAddress, bech32Address) => {
  const [erc20, bank] = await Promise.all([
    fetchEvmTokenBalance(evmAddress),
    fetchBankBalance(bech32Address),
  ]);
  return { erc20, bank, total: erc20 + bank };
};

/**
 * Fetches native NIBI balance for EVM wallet
 * @param {string} account - User's EVM address
 * @returns {Promise<number>} NIBI balance
 */
export const fetchEvmNibiBalance = async (account) => {
  try {
    if (!window.ethereum) {
      throw new Error("MetaMask is not installed");
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const balanceWei = await provider.getBalance(account);
    return Number(ethers.formatUnits(balanceWei, 18));
    
  } catch (error) {
    console.error("Error fetching NIBI balance:", error);
    return 0;
  }
};

/**
 * Checks and approves USDC spending for the PerpVault contract
 * @param {string} account - User's EVM address
 * @param {string} contractAddress - PerpVault contract address
 * @param {number} amount - Amount to approve (in USDC, not wei)
 * @returns {Promise<boolean>} Success status
 */
export const checkAndApproveUSDC = async (account, contractAddress, amount) => {
  try {
    console.log("Checking USDC approval:", {
      account,
      contractAddress,
      amount,
      type: typeof amount
    });
    
    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    
    const usdcContract = new ethers.Contract(
      process.env.REACT_APP_NIBIRU_USDC_CONTRACT_ADDRESS,
      [
        "function allowance(address,address) view returns (uint256)", 
        "function approve(address,uint256) returns (bool)",
        "function balanceOf(address) view returns (uint256)",
        "function decimals() view returns (uint8)"
      ],
      signer
    );
    
    // Check balance first
    const balance = await usdcContract.balanceOf(account);
    const balanceFormatted = Number(ethers.formatUnits(balance, 6));
    
    console.log("USDC balance check:", {
      balance: balanceFormatted,
      required: amount
    });
    
    if (balanceFormatted < amount) {
      throw new Error(`Insufficient USDC balance. Required: ${amount}, Available: ${balanceFormatted}`);
    }
    
    const allowance = await usdcContract.allowance(account, contractAddress);
    const requiredAmount = ethers.parseUnits(amount.toString(), 6);
    
    console.log("Allowance check:", {
      currentAllowance: ethers.formatUnits(allowance, 6),
      requiredAllowance: amount,
      needsApproval: allowance < requiredAmount
    });
    
    if (allowance < requiredAmount) {
      // toast.info("Approving USDC spending...");
      const approveTx = await executeWithGasEstimation(usdcContract, 'approve', [contractAddress, requiredAmount]);

      console.log("Approval transaction:", approveTx.hash);
      await approveTx.wait();
      
      // toast.success("USDC approved successfully");
    } else {
      console.log("USDC already approved");
    }
    
    return true;
  } catch (error) {
    console.error("USDC approval failed:", error);
    // toast.error("Failed to approve USDC: " + error.message);
    return false;
  }
};

/**
 * Parses trade ID from transaction receipt by looking for Wasm events
 * @param {Object} receipt - Transaction receipt
 * @returns {number|null} Trade ID or null if not found
 */
export const parseTradeIdFromReceipt = (receipt) => {
  const WASM_PRECOMPILE_ADDRESS = process.env.REACT_APP_NIBIRU_WASM_PRECOMPILE_ADDRESS || "0x0000000000000000000000000000000000000802";
  
  try {
    for (const log of receipt.logs) {
      if (log.address.toLowerCase() === WASM_PRECOMPILE_ADDRESS.toLowerCase()) {
        try {
          const decodedData = ethers.AbiCoder.defaultAbiCoder().decode(["string"], log.data);
          const attributesJsonString = decodedData[0];
          
          console.log("Wasm Event Raw JSON:", attributesJsonString);
          
          // Try to parse the JSON
          const attributes = JSON.parse(attributesJsonString);
          const eventType = attributes.eventType || attributes.action;
          
          // Look for trade registration events
          if (
            eventType === "wasm-sai/perp/register_trade" ||
            eventType === "wasm-sai/perp/store_trade" ||
            (eventType === "wasm" && (attributes.action === "register_trade" || attributes.action === "store_trade"))
          ) {
            let tradeDetails;
            
            // Parse trade details from different possible structures
            if (attributes.trade && typeof attributes.trade === "string") {
              tradeDetails = JSON.parse(attributes.trade);
            } else if (attributes.trade_info && typeof attributes.trade_info === "string") {
              tradeDetails = JSON.parse(attributes.trade_info);
            } else if (attributes.trade) {
              tradeDetails = attributes.trade;
            } else if (attributes.trade_info) {
              tradeDetails = attributes.trade_info;
            }
            
            if (tradeDetails && tradeDetails.user_trade_index) {
              const match = tradeDetails.user_trade_index.match(/UserTradeIndex\((\d+)\)/);
              if (match && match[1]) {
                return parseInt(match[1], 10);
              }
            } else if (tradeDetails && typeof tradeDetails.index === "number") {
              return tradeDetails.index;
            }
          }
        } catch (parseError) {
          console.warn("Error parsing Wasm event:", parseError.message);
        }
      }
    }
  } catch (error) {
    console.error("Error parsing trade ID from receipt:", error);
  }
  
  return null;
};

// Add dispatch validation helper
const validateDispatch = (dispatch) => {
  if (!dispatch || typeof dispatch !== 'function') {
    throw new Error("Invalid dispatch function provided");
  }
};

/**
 * Opens a new trade using the PerpVault EVM interface
 * @param {Object} opts - Trade options
 * @param {Object} dispatch - Redux dispatch function
 * @returns {Promise<Object>} Result object with success status and trade ID
 */
export const openTradeWithEvm = async (opts, dispatch) => {
  try {
    validateDispatch(dispatch);
    
    if (!window.ethereum) {
      toast.error("MetaMask is not installed");
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const account = await signer.getAddress();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      throw new Error("Missing REACT_APP_SAI_EVM_INTERFACE_ADDRESS environment variable");
    }

    // Validate required parameters
    const tradeAmount = parseFloat(opts.tradeAmount || "0");
    if (tradeAmount <= 0) {
      throw new Error("Trade amount must be greater than 0");
    }

    // Check USDC balance first
    const usdcBalance = await fetchEvmTokenBalance(account);

    console.log("USDC Balance Check:", {
      available: usdcBalance,
      required: tradeAmount,
      hasEnough: usdcBalance >= tradeAmount
    });

    if (usdcBalance < tradeAmount) {
      toast.error(`Insufficient USDC balance. Required: ${tradeAmount}, Available: ${usdcBalance}`);
      return { success: false, error: "Insufficient USDC balance" };
    }

    showTransactionToast("Placing order, just a second...");

    // Create contract instance
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);

    // toast.info("Preparing trade transaction...");
    
    // Parse market index
    let marketIndex = opts.marketIndex;
    if (typeof marketIndex === 'string' && marketIndex.includes('MarketIndex')) {
      marketIndex = parseInt(marketIndex.match(/\d+/)[0]);
    }
    
    // Create the Wasm message for openTrade following the exact format
    const openTradeJson = {
      open_trade: {
        market_index: `MarketIndex(${marketIndex})`,
        long: !!opts.isLong,
        collateral_index: `TokenIndex(${opts.collateralIndex || 3})`, // Default to USDC (3)
        leverage: opts.leverage?.toString() || "1",
        trade_type: opts.tradeType || "trade",
        open_price: opts.openPrice?.toString() || "69000",
        slippage_p: opts.slippage?.toString() || "0.01",
        is_evm_origin: true, // Indicate this is an EVM-originated trade
        client_origin: CLIENT_ORIGIN, // Public attribution: originating client
      }
    };
    
    // Add optional TP/SL if provided
    if (opts.takeProfitPrice) {
      openTradeJson.open_trade.tp = opts.takeProfitPrice.toString();
    }
    if (opts.stopLossPrice) {
      openTradeJson.open_trade.sl = opts.stopLossPrice.toString();
    }

    // Convert amounts to proper denominations
    const tradeAmountWei = ethers.parseUnits(tradeAmount.toString(), 6); // USDC has 6 decimals

    // collateral_amount (micro-USDC, Uint128) is a required field on the
    // on-chain OpenTrade message. Attached funds are deposited first, then this
    // pre-fee amount is resolved from the user's deposits/credits.
    openTradeJson.open_trade.collateral_amount = tradeAmountWei.toString();

    // Mandatory: guarantee client_origin is present before serialization.
    enforceClientOrigin(openTradeJson);

    console.log("Constructing openTrade message:", JSON.stringify(openTradeJson, null, 2));

    // Convert to bytes
    const wasmMsgExecute = ethers.toUtf8Bytes(JSON.stringify(openTradeJson));
    const useERC20Amount = tradeAmountWei; // Use all from ERC20
    
    console.log("Transaction parameters:", {
      wasmMsgExecute: JSON.stringify(openTradeJson),
      collateralIndex: opts.collateralIndex || 3,
      tradeAmount: tradeAmountWei.toString(),
      useERC20Amount: useERC20Amount.toString(),
      leverage: opts.leverage,
      contractAddress: contractAddress,
      account: account
    });
    
    // Execute the trade with dynamic gas estimation
    const tx = await executeWithGasEstimation(contract, 'openTrade', [
      wasmMsgExecute,
      opts.collateralIndex || 3, // collateralIndex
      tradeAmountWei,             // tradeAmount (bank denomination)
      useERC20Amount,             // useERC20Amount (ERC20 denomination)
    ]);
    
    // toast.info("Transaction submitted, waiting for confirmation...");
    console.log("Transaction sent:", tx.hash);
    
    // Wait for confirmation
    const receipt = await tx.wait();
    console.log("Transaction confirmed:", receipt);
    
    // Parse trade ID from receipt
    const tradeId = parseTradeIdFromReceipt(receipt);
    
    showTxSuccess(
      <div>
        Trade opened successfully!
        <br />
        <a
          href={getTransactionLink(receipt.hash)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:text-blue-700 underline"
        >
          View transaction
        </a>
      </div>
    );

    return {
      success: true,
      txHash: receipt.hash,
      tradeId: tradeId
    };

  } catch (error) {
    dismissTransactionToast();
    console.error("Error in openTradeWithEvm:", error);

    // Enhanced error logging
    if (error.receipt) {
      console.error("Transaction receipt:", error.receipt);
    }
    if (error.transaction) {
      console.error("Transaction details:", error.transaction);
    }

    // User-friendly error messages
    let errorMessage = error.message || "Unknown error";
    if (errorMessage.includes("user rejected")) {
      errorMessage = "Transaction was rejected by user";
    } else if (errorMessage.includes("insufficient funds")) {
      errorMessage = "Insufficient funds for gas fees";
    }

    return { success: false, error: errorMessage };
  }
};

// Helper function to get EVM transaction explorer URL (network-aware)
// Returns full URL for direct use
const getTransactionLink = (txHash) => {
  return getEvmExplorerUrl(txHash);
};

/**
 * Updates take profit price for a trade
 * @param {number} tradeId - ID of the trade
 * @param {string|number} newTakeProfit - New take profit price
 * @param {Object} dispatch - Redux dispatch function
 * @returns {Promise<Object>} Result object with success status
 */
export const updateTakeProfitEvm = async (tradeId, newTakeProfit, dispatch) => {
  try {
    validateDispatch(dispatch);

    if (!window.ethereum) {
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      return { success: false, error: "Contract address not configured" };
    }

    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);

    const updateTpJson = {
      update_tp: {
        trade_index: `UserTradeIndex(${tradeId})`,
        new_tp: newTakeProfit.toString(),
        new_sl: null
      }
    };

    const msgBytes = ethers.toUtf8Bytes(JSON.stringify(updateTpJson));

    const tx = await executeWithGasEstimation(contract, 'executeSimpleFunctions', [msgBytes]);

    const receipt = await tx.wait();

    return {
      success: true,
      txHash: receipt.hash
    };
  } catch (error) {
    console.error("Error updating take profit:", error);

    let errorMessage = error.message || "Unknown error";
    if (error.code === 'CALL_EXCEPTION') {
      errorMessage = "Transaction failed - check trade status and values";
    } else if (error.code === 'INSUFFICIENT_FUNDS') {
      errorMessage = "Insufficient gas or funds";
    } else if (errorMessage.includes("user rejected")) {
      errorMessage = "Transaction rejected by user";
    } else if (errorMessage.includes("execution reverted")) {
      errorMessage = "Transaction reverted - invalid trade parameters";
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Updates stop loss price for a trade
 * @param {number} tradeId - ID of the trade
 * @param {string|number} newStopLoss - New stop loss price
 * @param {Object} dispatch - Redux dispatch function
 * @returns {Promise<Object>} Result object with success status
 */
export const updateStopLossEvm = async (tradeId, newStopLoss, dispatch) => {
  try {
    validateDispatch(dispatch);

    if (!window.ethereum) {
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      return { success: false, error: "Contract address not configured" };
    }

    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);

    const updateSlJson = {
      update_tp: {
        trade_index: `UserTradeIndex(${tradeId})`,
        new_tp: null,
        new_sl: newStopLoss.toString()
      }
    };

    const msgBytes = ethers.toUtf8Bytes(JSON.stringify(updateSlJson));

    const tx = await executeWithGasEstimation(contract, 'executeSimpleFunctions', [msgBytes]);

    const receipt = await tx.wait();

    return {
      success: true,
      txHash: receipt.hash
    };
  } catch (error) {
    console.error("Error updating stop loss:", error);

    let errorMessage = error.message || "Unknown error";
    if (error.code === 'CALL_EXCEPTION') {
      errorMessage = "Transaction failed - check trade status and values";
    } else if (error.code === 'INSUFFICIENT_FUNDS') {
      errorMessage = "Insufficient gas or funds";
    } else if (errorMessage.includes("user rejected")) {
      errorMessage = "Transaction rejected by user";
    } else if (errorMessage.includes("execution reverted")) {
      errorMessage = "Transaction reverted - invalid trade parameters";
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Updates leverage for a trade
 * @param {number} tradeId - ID of the trade
 * @param {string|number} newLeverage - New leverage value
 * @param {Object} dispatch - Redux dispatch function
 * @returns {Promise<Object>} Result object with success status
 */
export const updateLeverageEvm = async (tradeId, newLeverage, dispatch) => {
  try {
    validateDispatch(dispatch);

    if (!window.ethereum) {
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      return { success: false, error: "Contract address not configured" };
    }

    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);

    const updateLeverageJson = {
      update_leverage: {
        trade_index: `UserTradeIndex(${tradeId})`,
        new_leverage: newLeverage.toString()
      }
    };

    const msgBytes = ethers.toUtf8Bytes(JSON.stringify(updateLeverageJson));

    const tx = await executeWithGasEstimation(contract, 'executeSimpleFunctions', [msgBytes]);

    const receipt = await tx.wait();

    return {
      success: true,
      txHash: receipt.hash
    };
  } catch (error) {
    console.error("Error updating leverage:", error);

    let errorMessage = error.message || "Unknown error";
    if (error.code === 'CALL_EXCEPTION') {
      errorMessage = "Transaction failed - check trade status and values";
    } else if (error.code === 'INSUFFICIENT_FUNDS') {
      errorMessage = "Insufficient gas or funds";
    } else if (errorMessage.includes("user rejected")) {
      errorMessage = "Transaction rejected by user";
    } else if (errorMessage.includes("execution reverted")) {
      errorMessage = "Transaction reverted - invalid trade parameters";
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Closes a trade using market order
 * @param {number} tradeId - ID of the trade to close
 * @param {boolean} sendFundsToERC20 - Whether to send funds to ERC20 or keep in bank (kept for compatibility)
 * @param {Object} dispatch - Redux dispatch function
 * @returns {Promise<Object>} Result object with success status
 */
export const closeTradeMarketEvm = async (tradeId, sendFundsToERC20 = true, dispatch) => {
  try {
    validateDispatch(dispatch);
    
    if (!window.ethereum) {
      // toast.error("MetaMask is not installed");
      return { success: false, error: "MetaMask not installed" };
    }

    showTransactionToast("Cancelling order, please wait...");

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      // toast.error("Missing contract address configuration");
      return { success: false, error: "Contract address not configured" };
    }

    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);

    // Validate tradeId
    if (typeof tradeId !== 'number' || tradeId < 0) {
      // toast.error("Invalid trade ID");
      return { success: false, error: "Invalid trade ID" };
    }
    
    console.log("Closing trade:", {
      tradeId,
      sendFundsToERC20,
      contractAddress
    });
    
    // toast.info("Closing trade, please wait for confirmation...");

    const wasmMsg = {
      close_trade: {
        trade_index: `UserTradeIndex(${tradeId.toFixed(0)})`,
      },
    };
    
    const wasmMsgBytes = ethers.toUtf8Bytes(JSON.stringify(wasmMsg));
    
    const tx = await executeWithGasEstimation(contract, 'executeSimpleFunctions', [wasmMsgBytes]);
    
    console.log("Transaction submitted:", tx.hash);
    
    // Wait for confirmation
    const receipt = await tx.wait();
    console.log("Transaction confirmed:", receipt);
    
    if (receipt.status === 0) {
      throw new Error("Transaction failed - check trade status and ownership");
    }
    
    showTxSuccess(
      <div>
        Trade closed successfully!
        <br />
        <a
          href={getTransactionLink(receipt.hash)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:text-blue-700 underline"
        >
          View transaction
        </a>
      </div>
    );
    dispatch(setPerpsUpdate());

    return {
      success: true,
      txHash: receipt.hash
    };
  } catch (error) {
    dismissTransactionToast();
    console.error("Error closing trade:", error);

    let errorMessage = error.message || "Unknown error";
    if (error.code === 'CALL_EXCEPTION') {
      if (error.receipt && error.receipt.status === 0) {
        errorMessage = "Transaction reverted - trade may not exist, already closed, or you don't own it";
      } else {
        errorMessage = "Transaction failed - check trade status and values";
      }
    } else if (error.code === 'INSUFFICIENT_FUNDS') {
      errorMessage = "Insufficient gas or funds";
    } else if (errorMessage.includes("user rejected")) {
      errorMessage = "Transaction rejected by user";
    } else if (errorMessage.includes("execution reverted")) {
      errorMessage = "Transaction reverted - trade cannot be closed";
    } else if (errorMessage.includes("Trade not found")) {
      errorMessage = "Trade not found or already closed";
    } else if (errorMessage.includes("invalid trade")) {
      errorMessage = "Invalid trade ID or trade does not belong to you";
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Gets the collateral denomination for a given index
 * @param {number} collateralIndex - The collateral index
 * @returns {Promise<string>} The denomination string
 */
export const getDenomOfCollateralIndex = async (collateralIndex) => {
  try {
    if (!window.ethereum) {
      throw new Error("MetaMask is not installed");
    }
    
    const provider = new ethers.BrowserProvider(window.ethereum);
    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    
    if (!contractAddress) {
      throw new Error("Missing contract address configuration");
    }
    
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, provider);
    
    const denom = await contract.getDenomOfCollateralIndex(collateralIndex);
    return denom;
  } catch (error) {
    console.error("Error getting collateral denom:", error);
    throw error;
  }
};

/**
 * Gets the collateral index for a specific trade
 * @param {string} userBech32Address - The user's Bech32 address
 * @param {number} tradeId - The trade ID
 * @returns {Promise<number>} The collateral index
 */
export const getCollateralIndexOfTrade = async (userBech32Address, tradeId) => {
  try {
    if (!window.ethereum) {
      throw new Error("MetaMask is not installed");
    }
    
    const provider = new ethers.BrowserProvider(window.ethereum);
    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    
    if (!contractAddress) {
      throw new Error("Missing contract address configuration");
    }
    
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, provider);
    
    const collateralIndex = await contract.getCollateralIndexOfTrade(userBech32Address, tradeId);
    return Number(collateralIndex);
  } catch (error) {
    console.error("Error getting collateral index of trade:", error);
    throw error;
  }
};

/**
 * Executes simple perp functions that don't require funds
 * @param {Object} wasmMessage - The Wasm message to execute
 * @returns {Promise<Object>} Result object with success status
 */
export const executeSimpleFunction = async (wasmMessage) => {
  try {
    if (!window.ethereum) {
      // toast.error("MetaMask is not installed");
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      // toast.error("Missing contract address configuration");
      return { success: false, error: "Contract address not configured" };
    }

    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);

    // Convert message to bytes
    const msgBytes = ethers.toUtf8Bytes(JSON.stringify(wasmMessage));

    // toast.info("Executing transaction, please wait...");

    // Execute the function
    const tx = await executeWithGasEstimation(contract, 'executeSimpleFunctions', [msgBytes]);

    // Wait for confirmation
    const receipt = await tx.wait();

    // toast.success("Transaction executed successfully");

    return {
      success: true,
      txHash: receipt.hash
    };
  } catch (error) {
    console.error("Error executing simple function:", error);

    let errorMessage = error.message || "Unknown error";
    if (errorMessage.includes("user rejected")) {
      errorMessage = "Transaction rejected by user";
    }

    // toast.error(`Failed to execute transaction: ${errorMessage}`);

    return {
      success: false,
      error: errorMessage
    };
  }
};


/**
 * Updates a limit order for a trade
 * @param {number} tradeId - ID of the trade
 * @param {string|number} price - New price for the limit order
 * @param {string|number} tp - New take profit (optional)
 * @param {string|number} sl - New stop loss (optional)
 * @param {string|number} slippage - Slippage percentage
 * @returns {Promise<Object>} Result object with success status
 */
export const updateLimitOrderEvm = async (tradeId, price, tp = null, sl = null, slippage = "0.5") => {
  try {
    if (!window.ethereum) {
      // toast.error("MetaMask is not installed");
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      // toast.error("Missing contract address configuration");
      return { success: false, error: "Contract address not configured" };
    }
    
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);
    
    // Construct the update_open_limit_order message
    const updateLimitOrderJson = {
      update_open_limit_order: {
        trade_index: `UserTradeIndex(${tradeId})`,
        price: price.toString(),
        slippage_p: slippage.toString()
      }
    };
    
    // Add optional TP/SL if provided
    if (tp !== null && tp !== undefined && tp !== "") {
      updateLimitOrderJson.update_open_limit_order.tp = tp.toString();
    }
    if (sl !== null && sl !== undefined && sl !== "") {
      updateLimitOrderJson.update_open_limit_order.sl = sl.toString();
    }
    
    // Convert to bytes
    const msgBytes = ethers.toUtf8Bytes(JSON.stringify(updateLimitOrderJson));
    
    // toast.info("Updating limit order, please wait...");
    
    // Execute using executeSimpleFunctions
    const tx = await executeWithGasEstimation(contract, 'executeSimpleFunctions', [msgBytes]);

    // Wait for confirmation
    const receipt = await tx.wait();

    // toast.success("Limit order updated successfully");

    return {
      success: true,
      txHash: receipt.hash
    };
  } catch (error) {
    console.error("Error updating limit order:", error);

    let errorMessage = error.message || "Unknown error";
    if (errorMessage.includes("user rejected")) {
      errorMessage = "Transaction rejected by user";
    } else if (errorMessage.includes("execution reverted")) {
      errorMessage = "Transaction reverted - check limit order parameters";
    }

    // toast.error(`Failed to update limit order: ${errorMessage}`);
    
    return { 
      success: false, 
      error: errorMessage
    };
  }
};

/**
 * Cancels a limit order for a trade
 * @param {number} tradeId - ID of the trade
 * @param {Object} dispatch - Redux dispatch function
 * @returns {Promise<Object>} Result object with success status
 */
export const cancelLimitOrderEvm = async (tradeId, dispatch) => {
  try {
    validateDispatch(dispatch);

    if (!window.ethereum) {
      // toast.error("MetaMask is not installed");
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      // toast.error("Missing contract address configuration");
      return { success: false, error: "Contract address not configured" };
    }
    
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);
    
    // Construct the cancel_open_limit_order message
    const cancelLimitOrderJson = {
      close_trade: {
        trade_index: `UserTradeIndex(${tradeId})`
      }
    };
    
    // Convert to bytes
    const msgBytes = ethers.toUtf8Bytes(JSON.stringify(cancelLimitOrderJson));
    
    showTransactionToast("Closing a Limit order, please wait...");

    const tx = await executeWithGasEstimation(contract, 'executeSimpleFunctions', [msgBytes]);

    const receipt = await tx.wait();

    showTxSuccess("Limit order cancelled successfully");
    dispatch(setPerpsUpdate());

    return {
      success: true,
      txHash: receipt.hash
    };
  } catch (error) {
    dismissTransactionToast();
    console.error("Error cancelling limit order:", error);

    let errorMessage = error.message || "Unknown error";
    if (errorMessage.includes("user rejected")) {
      errorMessage = "Transaction rejected by user";
    } else if (errorMessage.includes("execution reverted")) {
      errorMessage = "Transaction reverted - check limit order status";
    }

    showTxError(`Failed to cancel limit order: ${errorMessage}`);

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Increases position size for a trade
 * @param {number} tradeId - ID of the trade
 * @param {string|number} leverageDelta - Amount to increase leverage by
 * @param {string|number} openPrice - Open price for the increase
 * @param {string|number} slippage - Slippage percentage
 * @returns {Promise<Object>} Result object with success status
 */
export const increasePositionSizeEvm = async (tradeId, leverageDelta, openPrice, slippage = "0.5") => {
  try {
    if (!window.ethereum) {
      // toast.error("MetaMask is not installed");
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      // toast.error("Missing contract address configuration");
      return { success: false, error: "Contract address not configured" };
    }
    
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);
    
    // Construct the increase_position_size message
    const increasePositionJson = {
      increase_position_size: {
        trade_index: `UserTradeIndex(${tradeId})`,
        leverage_delta: leverageDelta.toString(),
        open_price: openPrice.toString(),
        slippage_p: slippage.toString()
      }
    };
    
    // Convert to bytes
    const msgBytes = ethers.toUtf8Bytes(JSON.stringify(increasePositionJson));
    
    // toast.info("Increasing position size, please wait...");
    
    // Note: This might need funds, but we'll try with executeSimpleFunctions first
    // If it fails, we might need to use a different approach with funding
    const tx = await executeWithGasEstimation(contract, 'executeSimpleFunctions', [msgBytes]);

    // Wait for confirmation
    const receipt = await tx.wait();

    // toast.success("Position size increased successfully");

    return {
      success: true,
      txHash: receipt.hash
    };
  } catch (error) {
    console.error("Error increasing position size:", error);

    let errorMessage = error.message || "Unknown error";
    if (errorMessage.includes("user rejected")) {
      errorMessage = "Transaction rejected by user";
    } else if (errorMessage.includes("execution reverted")) {
      errorMessage = "Transaction reverted - check position parameters";
    }

    // toast.error(`Failed to increase position size: ${errorMessage}`);
    
    return { 
      success: false, 
      error: errorMessage
    };
  }
};

/**
 * Decreases position size for a trade
 * @param {number} tradeId - ID of the trade
 * @param {string|number} leverageDelta - Amount to decrease leverage by
 * @param {string|number} collateralDelta - Amount of collateral to remove
 * @returns {Promise<Object>} Result object with success status
 */
export const decreasePositionSizeEvm = async (tradeId, leverageDelta, collateralDelta) => {
  try {
    if (!window.ethereum) {
      // toast.error("MetaMask is not installed");
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      // toast.error("Missing contract address configuration");
      return { success: false, error: "Contract address not configured" };
    }
    
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);
    
    // Construct the decrease_position_size message
    const decreasePositionJson = {
      decrease_position_size: {
        trade_index: `UserTradeIndex(${tradeId})`,
        leverage_delta: leverageDelta.toString(),
        collateral_delta: collateralDelta.toString()
      }
    };
    
    // Convert to bytes
    const msgBytes = ethers.toUtf8Bytes(JSON.stringify(decreasePositionJson));
    
    // toast.info("Decreasing position size, please wait...");
    
    // Execute using executeSimpleFunctions
    const tx = await executeWithGasEstimation(contract, 'executeSimpleFunctions', [msgBytes]);

    // Wait for confirmation
    const receipt = await tx.wait();

    // toast.success("Position size decreased successfully");

    return {
      success: true,
      txHash: receipt.hash
    };
  } catch (error) {
    console.error("Error decreasing position size:", error);

    let errorMessage = error.message || "Unknown error";
    if (errorMessage.includes("user rejected")) {
      errorMessage = "Transaction rejected by user";
    } else if (errorMessage.includes("execution reverted")) {
      errorMessage = "Transaction reverted - check position parameters";
    }

    // toast.error(`Failed to decrease position size: ${errorMessage}`);
    
    return { 
      success: false, 
      error: errorMessage
    };
  }
};

/**
 * Triggers a trade with specified order type
 * @param {number} tradeId - ID of the trade
 * @param {string} traderAddress - Trader address (can be EVM or bech32)
 * @param {string} orderType - Type of order to trigger
 * @returns {Promise<Object>} Result object with success status
 */
export const triggerTradeEvm = async (tradeId, traderAddress, orderType) => {
  try {
    if (!window.ethereum) {
      // toast.error("MetaMask is not installed");
      return { success: false, error: "MetaMask not installed" };
    }

    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();

    const contractAddress = process.env.REACT_APP_SAI_EVM_INTERFACE_ADDRESS;
    if (!contractAddress) {
      // toast.error("Missing contract address configuration");
      return { success: false, error: "Contract address not configured" };
    }
    
    const contract = new ethers.Contract(contractAddress, PERP_VAULT_INTERFACE_ABI, signer);
    
    // ✅ CONVERT EVM ADDRESS TO BECH32 IF NEEDED
    let bech32Address;
    if (traderAddress.startsWith('0x')) {
      console.log("Converting EVM address to bech32:", traderAddress);
      bech32Address = await convertEvmToBech32(traderAddress);
      console.log("Converted to bech32:", bech32Address);
    } else if (traderAddress.startsWith('nibi')) {
      bech32Address = traderAddress;
    } else {
      throw new Error("Invalid trader address format");
    }
    
    // ✅ VALIDATE INPUTS
    if (!bech32Address || !bech32Address.startsWith('nibi')) {
      throw new Error("Invalid trader bech32 address");
    }

    // ✅ VALIDATE ORDER TYPE
    const validOrderTypes = [
      "limit_open",
      "stop_open", 
      "tp_close",
      "sl_close",
      "liq_close"
    ];
    
    if (!validOrderTypes.includes(orderType)) {
      throw new Error(`Invalid order type: ${orderType}. Must be one of: ${validOrderTypes.join(', ')}`);
    }
    
    // Construct the trigger_trade message
    const triggerTradeJson = {
      trigger_trade: {
        trader: bech32Address,
        trade_index: `UserTradeIndex(${tradeId})`,
        order_type: orderType
      }
    };
    
    console.log("Trigger trade message:", JSON.stringify(triggerTradeJson, null, 2));
    
    // Convert to bytes
    const msgBytes = ethers.toUtf8Bytes(JSON.stringify(triggerTradeJson));
    
    // toast.info("Triggering trade, please wait...");
    
    // Execute using executeSimpleFunctions
    const tx = await executeWithGasEstimation(contract, 'executeSimpleFunctions', [msgBytes]);
    
    console.log("Trigger trade transaction:", tx.hash);
    
    // Wait for confirmation
    const receipt = await tx.wait();
    
    if (receipt.status === 0) {
      throw new Error("Transaction reverted - trade may not be eligible for triggering");
    }
    
    // toast.success("Trade triggered successfully");

    return {
      success: true,
      txHash: receipt.hash
    };
  } catch (error) {
    console.error("Error triggering trade:", error);

    let errorMessage = error.message || "Unknown error";

    if (error.code === 'CALL_EXCEPTION') {
      if (error.receipt && error.receipt.status === 0) {
        errorMessage = "Transaction reverted - trade may not be eligible for triggering or already executed";
      } else {
        errorMessage = "Transaction failed - check trade status and parameters";
      }
    } else if (error.code === 'INSUFFICIENT_FUNDS') {
      errorMessage = "Insufficient gas or funds";
    } else if (errorMessage.includes("user rejected")) {
      errorMessage = "Transaction rejected by user";
    } else if (errorMessage.includes("execution reverted")) {
      errorMessage = "Trade cannot be triggered - check trade state and order type";
    } else if (errorMessage.includes("Invalid order type")) {
      // Pass through validation errors
    } else if (errorMessage.includes("Invalid trader")) {
      // Pass through validation errors
    }

    // toast.error(`Failed to trigger trade: ${errorMessage}`);
    
    return { 
      success: false, 
      error: errorMessage
    };
  }
};